export type TimeMode = "single" | "compare" | "trend" | "risks";
export type TrendGranularity = "weekly" | "monthly";
export type ComparisonPreset = "wow" | "mom" | "custom";
export type QualityBand = "low" | "moderate" | "high";
export type WeekStartDay = "MON" | "SUN";

export const QUALITY_BAND_COLORS = {
  low: "#ff4d4f", // Red - F1 < 30%
  moderate: "#faad14", // Yellow - 30% <= F1 < 70%
  high: "#1b5e20", // Green - F1 >= 70%
} as const;

export const QUALITY_BAND_THRESHOLDS = {
  low: 30,
  moderate: 70,
} as const;

// Chart colors for flag distribution visualization
export const CHART_COLORS = {
  fn: "#ef4444", // Red - False Negatives (add_flag)
  fp: "#f87171", // Light Red - False Positives (flag_dismissed)
  tp: "#10b981", // Green - True Positives (flag_is_correct)
  grid: "#374151", // Gray - Chart grid
  axis: "#9ca3af", // Light Gray - Axis labels
  tooltipBg: "#1f2937", // Dark - Tooltip background
  tooltipBorder: "#374151", // Gray - Tooltip border
  positive: "#10b981", // Green - Positive delta
  negative: "#ef4444", // Red - Negative delta
  neutral: "#9ca3af", // Gray - Neutral/zero delta
} as const;

export type FlagQaRecord = {
  id: string;
  confidenceIndex: number;
  explanation: string;
  videoId: string;
  riskTypeShort: string;
  flagCreatedAt: string;
  actionTaken: string | null;
  feedbackReason: string | null;
  feedbackText: string | null;
  originalFlag: string | null;
  newFlag: string | null;
  merchantId: string;
  editCreatedAt: string | null;
  visualEvidence: string | null;
  transcriptEvidence: string | null;
};

export type SummaryRow = {
  category: string;
  fp: number;
  tp: number;
  fn: number;
  total: number;
  recall: number;
  precision: number;
  accuracy: number;
  f1: number;
};

export type PeriodSummary = {
  totalFlags: number;
  reviewedFlags: number;
  unreviewedFlags: number;
  fp: number;
  tp: number;
  fn: number;
  recall: number;
  precision: number;
  f1: number;
  accuracy: number;
  avgTimeToActionHours: number | null;
  qualityBand: QualityBand;
};

export type TrendDataPoint = {
  period: string;
  periodStart: string;
  periodEnd: string;
  totalFlags: number;
  reviewedFlags: number;
  f1: number;
  qualityBand: QualityBand;
  avgTimeToActionHours: number | null;
};

export type CategoryComparisonRow = {
  category: string;
  f1A: number;
  f1B: number;
  f1Delta: number;
};

export type RiskDimension = "newFlag" | "riskTypeShort";

export type RiskProblem = "falsePositives" | "falseNegatives" | "mixed";

export const RISK_PROBLEM_LABELS: Record<RiskProblem, string> = {
  falsePositives: "False positives",
  falseNegatives: "False negatives",
  mixed: "Mixed",
} as const;

export const RISK_PROBLEM_COLORS: Record<RiskProblem, string> = {
  falsePositives: "#d97706", // Amber - over-flagging, clean content dismissed
  falseNegatives: "#7c3aed", // Violet - misses, reviewers adding flags we didn't raise
  mixed: "#dc2626", // Red - both
} as const;

export type MerchantRiskRow = {
  merchantId: string;
  risk: string;
  total: number;
  fp: number;
  tp: number;
  fn: number;
  precision: number;
  recall: number;
  f1: number;
  problem: RiskProblem | null;
};
